import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, X, Settings } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'cookie_consent';

function getStoredConsent() {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    advertising: false,
  });

  useEffect(() => {
    const consent = getStoredConsent();
    if (!consent) {
      setVisible(true);
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      ...prefs,
      essential: true,
      timestamp: new Date().toISOString()
    }));
    setVisible(false);
  };

  const acceptAll = () => {
    saveConsent({ essential: true, analytics: true, marketing: true, advertising: true });
  };

  const rejectOptional = () => {
    saveConsent({ essential: true, analytics: false, marketing: false, advertising: false });
  };

  const saveSelected = () => {
    saveConsent(preferences);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] animate-slideUp" data-testid="cookie-banner">
      <div className="bg-slate-900 border-t border-slate-700 shadow-2xl">
        <div className="max-w-6xl mx-auto px-6 py-5">
          {/* Main banner */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Shield className="text-rose-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-sm text-slate-200 leading-relaxed">
                  Utilizamos cookies propias y de terceros para mejorar tu experiencia, analizar el uso del sitio y personalizar contenido.
                  Puedes aceptar todas, rechazar las opcionales o configurar tus preferencias.{' '}
                  <Link to="/legal/cookies" className="text-rose-400 underline hover:text-rose-300">
                    Política de Cookies
                  </Link>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0 w-full lg:w-auto">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="flex items-center gap-1.5 px-4 py-2.5 border border-slate-600 text-slate-300 hover:text-white hover:border-slate-400 transition text-xs font-medium"
                data-testid="cookie-settings-btn"
              >
                <Settings size={14} />
                Configurar
              </button>
              <button
                onClick={rejectOptional}
                className="px-4 py-2.5 border border-slate-600 text-slate-300 hover:text-white hover:border-slate-400 transition text-xs font-medium"
                data-testid="cookie-reject-btn"
              >
                Rechazar opcionales
              </button>
              <button
                onClick={acceptAll}
                className="px-5 py-2.5 bg-rose-600 text-white hover:bg-rose-700 transition text-xs font-bold"
                data-testid="cookie-accept-btn"
              >
                Aceptar todas
              </button>
            </div>
          </div>

          {/* Settings panel */}
          {showSettings && (
            <div className="mt-4 pt-4 border-t border-slate-700" data-testid="cookie-settings-panel">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                {/* Essential - always on */}
                <div className="bg-slate-800 p-3 border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-slate-200">Esenciales</span>
                    <span className="text-[10px] text-green-400 font-medium">Siempre activas</span>
                  </div>
                  <p className="text-[10px] text-slate-400 leading-relaxed">
                    Necesarias para el funcionamiento del sitio. Incluyen autenticación, seguridad y preferencias básicas.
                  </p>
                </div>

                {/* Analytics */}
                <div className="bg-slate-800 p-3 border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-slate-200">Analíticas</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences(p => ({ ...p, analytics: e.target.checked }))}
                        className="sr-only peer"
                        data-testid="cookie-toggle-analytics"
                      />
                      <div className="w-8 h-4 bg-slate-600 peer-checked:bg-rose-500 rounded-full transition peer-focus:ring-2 peer-focus:ring-rose-400 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-4" />
                    </label>
                  </div>
                  <p className="text-[10px] text-slate-400 leading-relaxed">
                    Nos permiten medir el uso del sitio para mejorar su contenido y rendimiento.
                  </p>
                </div>

                {/* Marketing */}
                <div className="bg-slate-800 p-3 border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-slate-200">Marketing</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences(p => ({ ...p, marketing: e.target.checked }))}
                        className="sr-only peer"
                        data-testid="cookie-toggle-marketing"
                      />
                      <div className="w-8 h-4 bg-slate-600 peer-checked:bg-rose-500 rounded-full transition peer-focus:ring-2 peer-focus:ring-rose-400 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-4" />
                    </label>
                  </div>
                  <p className="text-[10px] text-slate-400 leading-relaxed">
                    Para enviar comunicaciones comerciales personalizadas sobre nuestros servicios.
                  </p>
                </div>

                {/* Advertising */}
                <div className="bg-slate-800 p-3 border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-slate-200">Publicidad</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.advertising}
                        onChange={(e) => setPreferences(p => ({ ...p, advertising: e.target.checked }))}
                        className="sr-only peer"
                        data-testid="cookie-toggle-advertising"
                      />
                      <div className="w-8 h-4 bg-slate-600 peer-checked:bg-rose-500 rounded-full transition peer-focus:ring-2 peer-focus:ring-rose-400 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-4" />
                    </label>
                  </div>
                  <p className="text-[10px] text-slate-400 leading-relaxed">
                    Para mostrar anuncios relevantes en plataformas de terceros.
                  </p>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={saveSelected}
                  className="px-5 py-2.5 bg-white text-slate-900 hover:bg-slate-100 transition text-xs font-bold"
                  data-testid="cookie-save-btn"
                >
                  Guardar preferencias
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slideUp { animation: slideUp 0.4s ease-out; }
      `}</style>
    </div>
  );
}
